import { createContext, useContext, useEffect, useState } from 'react';
import { useOrganization } from '@/lib/organization-context';
import { supabase } from '@/lib/supabase';
import { useQuery } from '@tanstack/react-query';

interface Branch {
  id: string;
  organization_id: string;
  name: string;
  is_default: boolean;
  region_rules: any;
  color: string | null;
}

interface BranchContextType {
  branches: Branch[];
  currentBranch: Branch | null;
  setCurrentBranch: (branch: Branch | null) => void;
  loading: boolean;
  hasMultipleBranches: boolean;
}

const BranchContext = createContext<BranchContextType | undefined>(undefined);

export function BranchProvider({ children }: { children: React.ReactNode }) {
  const { currentOrganization } = useOrganization();
  const [currentBranch, setCurrentBranchState] = useState<Branch | null>(null);

  const { data: branches = [], isLoading } = useQuery<Branch[]>({
    queryKey: ['branches', currentOrganization?.id],
    enabled: !!currentOrganization,
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      if (!currentOrganization) return [];
      const { data, error } = await supabase
        .from('branches')
        .select('*')
        .eq('organization_id', currentOrganization.id);
      if (error) throw error;
      return data ?? [];
    },
  });

  const loading = isLoading && !!currentOrganization;

  // When branches load or org changes, restore selection from localStorage or pick default
  useEffect(() => {
    if (!branches.length) {
      setCurrentBranchState(null);
      return;
    }

    const storedBranchId = typeof window !== 'undefined'
      ? localStorage.getItem('currentBranchId')
      : null;

    if (storedBranchId) {
      const stored = branches.find(b => b.id === storedBranchId);
      if (stored) {
        setCurrentBranchState(stored);
        return;
      }
    }

    // Fall back to the default branch
    const defaultBranch = branches.find(b => b.is_default);
    setCurrentBranchState(defaultBranch ?? branches[0]);
  }, [branches]);

  // Reset when org changes
  useEffect(() => {
    setCurrentBranchState(null);
    if (typeof window !== 'undefined') {
      localStorage.removeItem('currentBranchId');
    }
  }, [currentOrganization?.id]);

  const setCurrentBranch = (branch: Branch | null) => {
    setCurrentBranchState(branch);
    if (typeof window !== 'undefined') {
      if (branch) {
        localStorage.setItem('currentBranchId', branch.id);
      } else {
        localStorage.removeItem('currentBranchId');
      }
    }
  };

  return (
    <BranchContext.Provider value={{
      branches,
      currentBranch,
      setCurrentBranch,
      loading,
      hasMultipleBranches: branches.length > 1,
    }}>
      {children}
    </BranchContext.Provider>
  );
}

export function useBranch() {
  const context = useContext(BranchContext);
  if (context === undefined) {
    throw new Error('useBranch must be used within a BranchProvider');
  }
  return context;
}
